import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TextField,
} from "@mui/material";
import {
  CustomHeadCell,
  MovementTypeHeaderCell,
} from "components/utils/LiquidationDetails";
import { arsFormatter, formatDateOnlyDMY } from "helpers";
import React, { useMemo } from "react";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import {
  LiquidationAggregation,
  LiquidationMovementAdditionalItem,
  MovementLiquidation,
  PendingAdditionalChange,
} from "types";

type Props = {
  row: MovementLiquidation;
  liquidation: LiquidationAggregation;
  print: boolean;
  hasDetail: boolean;
  pendingChanges: PendingAdditionalChange[];
  onChange: (change: PendingAdditionalChange) => void;
};

export default function Row({
  row,
  liquidation,
  print,
  hasDetail,
  pendingChanges,
  onChange,
}: Props) {
  const [open, setOpen] = React.useState(print);

  const additionalItems = useMemo(() => {
    return liquidation.liquidation_movement_additional_items.filter(
      (mai) => mai.movement_id === row.movement_id,
    );
  }, [row.movement_id, liquidation.liquidation_movement_additional_items]);

  const additionalItemsMap = useMemo(() => {
    return new Map(
      liquidation.liquidation_additional_items.map((item) => [
        item.additional_item_id,
        item,
      ]),
    );
  }, [liquidation.liquidation_additional_items]);

  const getPendingMovementChange = (
    additionalItemId: number,
    movementId?: number | null,
  ) => {
    return pendingChanges.find(
      (change) =>
        change.additional_item_id === additionalItemId &&
        change.movement_id === movementId,
    );
  };

  const getPendingPriceChange = (additionalItemId: number) => {
    return pendingChanges.find(
      (change) =>
        change.additional_item_id === additionalItemId &&
        change.price !== undefined,
    );
  };

  const getDisplayAmount = (
    movementAdditionalItem: LiquidationMovementAdditionalItem,
  ) => {
    const pendingChange = getPendingMovementChange(
      movementAdditionalItem.additional_item_id,
      movementAdditionalItem.movement_id,
    );

    return pendingChange?.amount ?? movementAdditionalItem.amount;
  };

  const getDisplayPrice = (
    movementAdditionalItem: LiquidationMovementAdditionalItem,
  ) => {
    const pendingPriceChange = getPendingPriceChange(
      movementAdditionalItem.additional_item_id,
    );

    if (pendingPriceChange?.price !== undefined) {
      return pendingPriceChange.price;
    }

    return (
      additionalItemsMap.get(movementAdditionalItem.additional_item_id)
        ?.price ?? 0
    );
  };

  const getDisplaySubtotal = (
    movementAdditionalItem: LiquidationMovementAdditionalItem,
  ) => {
    const pendingChange = getPendingMovementChange(
      movementAdditionalItem.additional_item_id,
      movementAdditionalItem.movement_id,
    );

    return pendingChange?.subtotal ?? movementAdditionalItem.subtotal;
  };

  const subtotal = useMemo(() => {
    return additionalItems.reduce((acc, item) => {
      return acc + getDisplaySubtotal(item);
    }, 0);
  }, [additionalItems, pendingChanges]);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          {!print && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <BsArrowUp /> : <BsArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{MovementTypeHeaderCell(row.movement_type_id)}</TableCell>
        <TableCell>{row.movement_id}</TableCell>
        <TableCell>{row.client_document}</TableCell>
        <TableCell>{formatDateOnlyDMY(row.date)}</TableCell>
        <TableCell>{row.associated_client}</TableCell>
        <TableCell>{arsFormatter(subtotal)}</TableCell>
      </TableRow>

      {hasDetail && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Detalle del Movimiento
                </Typography>

                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Cantidad (Unidades)</TableCell>
                      <TableCell>Precio</TableCell>
                      <TableCell>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {additionalItems.map((movementAdditionalItem, index) => {
                      const additionalItem = additionalItemsMap.get(
                        movementAdditionalItem.additional_item_id,
                      );

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {additionalItem?.description ?? "-"}
                          </TableCell>

                          <TableCell>
                            {getDisplayAmount(movementAdditionalItem)}
                          </TableCell>

                          <TableCell>
                            {print ? (
                              arsFormatter(
                                getDisplayPrice(movementAdditionalItem),
                              )
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                value={getDisplayPrice(movementAdditionalItem)}
                                onChange={(e) => {
                                  const value = Number(e.target.value);

                                  onChange({
                                    additional_item_id:
                                      movementAdditionalItem.additional_item_id,
                                    price: value,
                                  });
                                }}
                                inputProps={{ min: 0, step: "0.01" }}
                              />
                            )}
                          </TableCell>

                          <TableCell>
                            {getDisplaySubtotal(movementAdditionalItem)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}
